import { getEtMarketDateKey, getMarketSession, isUsEquityTradingDay, isUsEquityEarlyCloseDay } from '../marketSession.js'
import { boundedMemo } from '../boundedMemo.js'

const closeHourFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', hourCycle: 'h23' })

const price = value => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null
const pct = (value, reference) => value != null && reference != null ? (value / reference - 1) * 100 : null

// A completed-session identity, not yesterday's calendar date. Daily history
// supplies the actual trading dates, including holidays and exceptional gaps.
const closeBoundaryForMinute = boundedMemo(minute => {
  const now = minute * 60_000
  const date = getEtMarketDateKey(now)
  const parts = closeHourFormatter.formatToParts(new Date(now))
  const hour = Number(parts.find(part => part.type === 'hour')?.value)
  const completedToday = isUsEquityTradingDay(now) && hour >= (isUsEquityEarlyCloseDay(now) ? 13 : 16)
  return { date, completedToday, key: `${date}:${completedToday ? 'after-close' : 'before-close'}` }
}, 256)

export function equityCloseBoundary(now) {
  // Callers still receive their own object; cached session facts stay private.
  return { ...closeBoundaryForMinute(Math.floor(new Date(now).getTime() / 60_000)) }
}

export function selectEquityCloses(bars, now) {
  const { date, completedToday } = equityCloseBoundary(now)
  const dated = new Map()
  for (const bar of bars || []) {
    const raw = String(bar.time ?? '')
    if (!/^\d{8}$/.test(raw)) continue
    const day = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`
    const close = price(bar.close)
    if (close != null && (day < date || (completedToday && day === date))) dated.set(day, close)
  }
  const sessionDate = new Date(`${date}T16:00:00Z`)
  if (!completedToday) sessionDate.setUTCDate(sessionDate.getUTCDate() - 1)
  while (!isUsEquityTradingDay(sessionDate)) sessionDate.setUTCDate(sessionDate.getUTCDate() - 1)
  const latestDate = getEtMarketDateKey(sessionDate)
  sessionDate.setUTCDate(sessionDate.getUTCDate() - 1)
  while (!isUsEquityTradingDay(sessionDate)) sessionDate.setUTCDate(sessionDate.getUTCDate() - 1)
  const previousDate = getEtMarketDateKey(sessionDate)
  return { latest: dated.has(latestDate) ? { date: latestDate, price: dated.get(latestDate) } : null,
    previous: dated.has(previousDate) ? { date: previousDate, price: dated.get(previousDate) } : null }
}

export function resolveEquityPriceDisplay(snapshot, closes, now, session) {
  const regular = getMarketSession(now) === 'regular'
  const latest = price(closes?.latest?.price)
  const previous = price(closes?.previous?.price)
  const lastTrade = snapshot?.freshness === 'unavailable' ? null : price(snapshot?.last)
  const extended = ['pre', 'post', 'pre-market', 'post-market', 'overnight'].includes(session)
  const last = regular ? lastTrade : latest
  const changePercent = pct(last, regular ? latest : previous)
  const extendedHours = extended ? pct(lastTrade, latest) : null
  return {
    last, changePercent, extendedHours, session,
    regularReference: regular ? latest : previous,
    headlinePrice: extended ? lastTrade : last,
    headlineReference: extended || regular ? latest : previous,
    headlineChangePercent: extended ? extendedHours : changePercent,
    closePrice: latest,
    freshness: snapshot?.freshness || 'unavailable',
    freshnessReason: snapshot?.freshnessReason ?? null,
    regularDate: closes?.latest?.date ?? null,
  }
}
